//#region Helper Imports

import { DataProvider, ConvertHelper, ToastHelper } from "../helpers"
import { DataProviderRequest } from "./utils";

//#endregion

//#region Model Imports

import { Enums } from "../models/common"
import { OpLocation, OpLocationEquipment, OpFile } from "../models/core"
import { ColumnInfo, OpSchema, OpValidateInfo, OpActionResult, OpLocationConfigurationProfile } from "../models/custom"
import { OpExportPluginParam } from "../models/util";
import { OpProblemClass } from "../models/dw";

//#endregion


export class DataProviderLocation {

    //#region Ctor

    public constructor(private dataProvider: DataProvider) { }

    // #endregion

    //#region Configuration Profile

    public getLocationConfigurationProfile = (idLocation: number): JQueryPromise<OpLocationConfigurationProfile> => {
        return this.dataProvider
            .sendRequestWithSession('OpxGetLocationConfigurationProfile', { IdLocation: idLocation }, 'OpxGetLocationConfigurationProfileResult')
            .then((data: any) => {
                return new OpLocationConfigurationProfile(data)
            })
    }


    public saveLocationConfigurationProfile = (locationConfigurationProfile: OpLocationConfigurationProfile): JQueryPromise<OpLocationConfigurationProfile> => {
        return this.dataProvider
            .sendRequestWithSession('OpxSaveLocationConfigurationProfile', { LocationConfigurationProfile: locationConfigurationProfile.toJSON() }, 'OpxSaveLocationConfigurationProfileResult')
            .then((data: any) => {
                return new OpLocationConfigurationProfile(data)
            })
    }

    public sendLocationConfiguration = (idLocations: number[], idProfile: number): JQueryPromise<OpActionResult[]> => {
        return this.dataProvider
            .sendRequestWithSession('OpxSendLocationConfiguration', { locationIds: idLocations, idConfigurationProfile: idProfile }, 'OpxSendLocationConfigurationResult')
            .then((data: any[]) => data.map(dataItem => new OpActionResult(dataItem)))
    }

    //#endregion

    //#region Delete

    public deleteLocation = (opObj: OpLocation): JQueryPromise<OpLocation> => {
        opObj.opState = Enums.OpChangeState.Delete
        opObj.dataList.setValue(Enums.LocationStateType.DELETED, Enums.DataType.HELPER_ID_LOCATION_STATE_TYPE)

        return this.saveLocation(opObj)
    }

    //#endregion

    //#region Delivery

    public locationPlanDeliveryTime = (idLocations: ReadonlyArray<number>, deliveryDateTime: Date = null): DataProviderRequest<void> => {
        const deliveryDateTimeFormatted: string = deliveryDateTime != null ? ConvertHelper.dateToString(deliveryDateTime, { netFormat: true }) : null
        const promise = this.dataProvider
            .sendRequestWithSession('OpxLocationPlanDeliveryTime', { IdLocation: idLocations, DeliveryDateTime: deliveryDateTimeFormatted }, null)

        return DataProviderRequest.fromJQuery(promise)
    }

    //#endregion

    //#region Equipment

    public changeLocationEquipment = (locationEquipment: OpLocationEquipment[]) => {
        return this.dataProvider.sendRequestWithSession('OpxChangeLocationEquipment', {
            EquipmentToChange: locationEquipment.map((equipmentItem: OpLocationEquipment) => equipmentItem.toJSON())
        }, 'OpxChangeLocationEquipmentResult')
    }

    public getLocationEquipment = (idLocation: number): DataProviderRequest<OpLocationEquipment[]> => {
        const promise: JQueryPromise<OpLocationEquipment[]> = this.dataProvider
            .sendRequestWithSession('OpxGetLocationEquipment', { IdLocation: idLocation, OnlyCurrent: true }, 'OpxGetLocationEquipmentResult')
            .then((response: any) => response.map((opObj: any) => new OpLocationEquipment(opObj)))

        return DataProviderRequest.fromJQuery(promise)
    }

    //#endregion

    //#region Export

    public locationExport = (referenceType: Enums.ReferenceType, columns: ColumnInfo[], customWhereClause: string, dllName: string, params: OpExportPluginParam[]) => {
        const requestArgs: any = {
            ReferenceType: referenceType,
            Columns: columns,
            CustomWhereClause: customWhereClause,
            DllName: dllName,
            exportParams: params != null ? params.map((param: OpExportPluginParam) => param.toJSON()) : null
        }

        return this.dataProvider
            .sendRequestWithSession('OpxLocationExport', requestArgs, 'OpxLocationExportResult')
            .then((data: any) => {
                if (data != null) {
                    return new OpFile(data)
                }

                return null
            })
    }

    //#endregion

    //#region Get

    public getLocation = (id: number): DataProviderRequest<OpLocation> => {
        const promise: JQueryPromise<OpLocation> = this.dataProvider
            .sendRequestWithSession('OpxGetLocation', { IdLocation: id }, 'OpxGetLocationResult')
            .then((response: any) => {
                const result: OpLocation = new OpLocation(response)

                OpLocation.loadNavigationProperties(this.dataProvider, [result])

                return result
            })
            .fail((error) => {
                const msg: string = ConvertHelper.getRequestErrorMessage(error)
                ToastHelper.showError(msg)
            })

        return DataProviderRequest.fromJQuery(promise)
    }



    public getLocationFilter = (columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string) => {
        return this.dataProvider.sendFilterRequestWithSession<OpLocation>(columns, pageNbr, pageSize, customWhereClause, Enums.ReferenceType.IdLocation, 'OpxLocationFilter', 'OpxLocationFilterResult')
    }

    public getAllLocationFilter = (columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string) => {
        return this.dataProvider.sendFilterRequestWithSession(columns, pageNbr, pageSize, customWhereClause, Enums.ReferenceType.IdLocation, 'OpxAllLocationFilter ', 'OpxAllLocationFilterResult')
    }

    //#endregion

    //#region Groups

    public getLocationAssignedGroup = (idLoction: number[], onlyLastGroup: boolean): JQueryPromise<OpLocation[]> => {
        return this.dataProvider
            .sendRequestWithSession('OpxGetLocationAssignedGroup', { idLoction: idLoction, onlyLastGroup: onlyLastGroup }, 'OpxGetLocationAssignedGroupResult')
            .then((data: any) => {
                if (data != null) {
                    const locations: OpLocation[] = data.map((obj: any) => new OpLocation(obj))

                    OpLocation.loadNavigationProperties(this.dataProvider, locations)

                    return locations
                }

                return null
            })
    }

    //#endregion

    //#region Schema

    public getLocationSchemas = (id: number): JQueryPromise<OpSchema[]> => {
        return this.dataProvider
            .sendRequestWithSession('OpxGetLocationSchema', { IdLocation: id }, 'OpxGetLocationSchemaResult')
            .then((response: any[]) => DataProvider.convertArrayResult<OpSchema>(response, OpSchema))
    }

    //#endregion

    //#region Save

    public saveLocation = (opObj: OpLocation): JQueryPromise<OpLocation> => {
        return this.dataProvider
            .sendRequestWithSession('OpxSaveLocation', { ToBeSaved: opObj.toJSON() }, 'OpxSaveLocationResult')
            .then((response: any) => {
                const result: OpLocation = new OpLocation(response)

                OpLocation.loadNavigationProperties(this.dataProvider, [result])

                return result
            })
    }

    //#endregion

    //#region Problems

    public confirmLocationProblem = (idLocations: ReadonlyArray<number>, problemClass: ReadonlyArray<Enums.ProblemClass>): DataProviderRequest<void> => {
        const promise = this.dataProvider.sendRequestWithSession('OpxConfirmLocationProblem', { IdLocation: idLocations, ProblemClass: problemClass }, null)

        return DataProviderRequest.fromJQuery(promise)
    }

    public getLocationProblemClass = (idLocations: ReadonlyArray<number>): DataProviderRequest<OpProblemClass[]> => {
        const promise = this.dataProvider
            .sendRequestWithSession('OpxGetLocationProblemClass', { IdLocation: idLocations }, 'OpxGetLocationProblemClassResult')
            .then((result: any[]) => {
                if (result != null) {
                    return result.map((obj: any) => new OpProblemClass(obj))
                }

                return null
            })

        return DataProviderRequest.fromJQuery(promise)
    }

    //#endregion

    //#region Validate

    public validateLocation = (locations: OpLocation[]): JQueryPromise<OpValidateInfo[]> => {
        return this.dataProvider
            .sendRequestWithSession('OpxValidateLocation', { Location: locations }, 'OpxValidateLocationResult')
            .then((response: any[]) => DataProvider.convertArrayResult<OpValidateInfo>(response, OpValidateInfo))
    }

    //#endregion

}